import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyle, FormRow, fmtQty } from './InventoryShared';

export default function InventoryTransfers() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/inventory/transfers?per_page=50`;
      if (status) url += `&status=${status}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status]);

  const fetchAux = useCallback(async () => {
    const [pRes, wRes] = await Promise.all([
      fetch(`${API}/companies/${companyId}/inventory/products?per_page=200`, { headers: getAuthHeaders() }),
      fetch(`${API}/companies/${companyId}/inventory/warehouses`, { headers: getAuthHeaders() }),
    ]);
    const pJson = await pRes.json();
    setProducts(Array.isArray(pJson.data) ? pJson.data : []);
    setWarehouses(await wRes.json());
  }, [companyId]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchAux(); }, [fetchAux]);

  const openView = async (id) => {
    const res = await fetch(`${API}/companies/${companyId}/inventory/transfers/${id}`, { headers: getAuthHeaders() });
    setViewing(await res.json());
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>🚚 Stock Transfers</h2>
        <button style={btnPrimary} onClick={() => setCreating(true)}>+ New Transfer</button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <select style={{ ...inputStyle, width: 200 }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="confirmed">Confirmed</option>
          <option value="dispatched">Dispatched</option>
          <option value="partially_received">Partially Received</option>
          <option value="received">Received</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Transfer #', 'From', 'To', 'Lines', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No transfers</td></tr>}
              {list.map(t => (
                <tr key={t.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>
                    <a href="#v" onClick={e => { e.preventDefault(); openView(t.id); }} style={{ color: '#3b82f6' }}>{t.transfer_number}</a>
                  </td>
                  <td style={tdStyle}>{t.from_warehouse?.name}</td>
                  <td style={tdStyle}>{t.to_warehouse?.name}</td>
                  <td style={tdStyle}>{t.lines_count ?? 0}</td>
                  <td style={tdStyle}><Badge value={t.status} /></td>
                  <td style={tdStyle}><button style={btnSmall} onClick={() => openView(t.id)}>Open</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <CreateTransferModal companyId={companyId} products={products} warehouses={warehouses}
          onClose={() => setCreating(false)} onSaved={() => { setCreating(false); fetchList(); }} />
      )}
      {viewing && (
        <TransferDetail companyId={companyId} transfer={viewing}
          onClose={() => { setViewing(null); fetchList(); }} onRefresh={() => openView(viewing.id)} />
      )}
    </div>
  );
}

function CreateTransferModal({ companyId, products, warehouses, onClose, onSaved }) {
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [lines, setLines] = useState([{ product_id: '', quantity: '' }]);
  const [saving, setSaving] = useState(false);

  const addLine = () => setLines([...lines, { product_id: '', quantity: '' }]);
  const updateLine = (i, field, val) => setLines(lines.map((l, idx) => idx === i ? { ...l, [field]: val } : l));
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!fromId || !toId || fromId === toId) { alert('Select two different warehouses'); return; }
    const validLines = lines.filter(l => l.product_id && l.quantity);
    if (validLines.length === 0) { alert('At least one line is required'); return; }
    setSaving(true);
    const res = await fetch(`${API}/companies/${companyId}/inventory/transfers`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ from_warehouse_id: fromId, to_warehouse_id: toId, lines: validLines }),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
    onSaved();
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 600 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>New Transfer</h3>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}><FormRow label="From Warehouse *">
            <select style={inputStyle} value={fromId} onChange={e => setFromId(e.target.value)}>
              <option value="">— Select —</option>
              {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </FormRow></div>
          <div style={{ flex: 1 }}><FormRow label="To Warehouse *">
            <select style={inputStyle} value={toId} onChange={e => setToId(e.target.value)}>
              <option value="">— Select —</option>
              {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </FormRow></div>
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', margin: '10px 0 6px' }}>LINES</div>
        {lines.map((l, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <select style={{ ...inputStyle, flex: 2 }} value={l.product_id} onChange={e => updateLine(i, 'product_id', e.target.value)}>
              <option value="">— Product —</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <input type="number" placeholder="Qty" style={{ ...inputStyle, flex: 1 }} value={l.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} />
            <button style={btnSmall} onClick={() => removeLine(i)}>✕</button>
          </div>
        ))}
        <button style={btnSecondary} onClick={addLine}>+ Add Line</button>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Create'}</button>
        </div>
      </div>
    </div>
  );
}

function TransferDetail({ companyId, transfer, onClose, onRefresh }) {
  const [busy, setBusy] = useState(false);

  const act = async (action) => {
    setBusy(true);
    const res = await fetch(`${API}/companies/${companyId}/inventory/transfers/${transfer.id}/${action}`, { method: 'POST', headers: getAuthHeaders() });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Action failed'); }
    setBusy(false); onRefresh();
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 600 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{transfer.transfer_number} <Badge value={transfer.status} /></h3>
        <div style={{ marginBottom: 10 }}><b>{transfer.from_warehouse?.name}</b> → <b>{transfer.to_warehouse?.name}</b></div>
        <table style={tableStyle}>
          <thead><tr>{['Product', 'Qty', 'Dispatched', 'Received'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
          <tbody>
            {(transfer.lines || []).map(l => (
              <tr key={l.id}>
                <td style={tdStyle}>{l.product?.name || l.product_id}</td>
                <td style={tdStyle}>{fmtQty(l.quantity)}</td>
                <td style={tdStyle}>{fmtQty(l.dispatched_qty)}</td>
                <td style={tdStyle}>{fmtQty(l.received_qty)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          {transfer.status === 'draft' && <button style={btnSecondary} onClick={() => act('confirm')} disabled={busy}>Confirm</button>}
          {['draft', 'confirmed'].includes(transfer.status) && <button style={btnPrimary} onClick={() => act('dispatch')} disabled={busy}>Dispatch</button>}
          {['dispatched', 'partially_received'].includes(transfer.status) && <button style={btnPrimary} onClick={() => act('receive')} disabled={busy}>Receive All</button>}
          {['draft', 'confirmed'].includes(transfer.status) && <button style={btnSecondary} onClick={() => act('cancel')} disabled={busy}>Cancel</button>}
          <button style={btnSecondary} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
